import { useMemo } from 'react';
import type { RateMultiplier } from '@/types';

interface OvertimeLadderInfo {
  /** Non-STRAIGHT_TIME multipliers that have at least one threshold configured. */
  thresholdMultipliers: RateMultiplier[];
  /** The highest rate among threshold multipliers (falls back to 1 for STRAIGHT_TIME-only ladders). */
  highestOvertimeRate: number;
  /** Display name of the multiplier tier with the highest rate (e.g. "Double Time"). */
  highestOvertimeTierName: string | undefined;
  /** Maximum useful extended overtime tiers (ladder bracket count). */
  maxExtendedTiers: number;
}

export function getOvertimeLadderInfo(
  rateMultipliers: RateMultiplier[],
  highestOvertimeRateOverride?: number | null,
): OvertimeLadderInfo {
  const thresholdMultipliers = rateMultipliers.filter(m =>
    m.code !== 'STRAIGHT_TIME' &&
    (m.defaultPerShiftThresholdHours != null || m.defaultPerWeekThresholdHours != null)
  );

  const highestOvertimeRate = highestOvertimeRateOverride != null
    ? highestOvertimeRateOverride
    : thresholdMultipliers.length > 0
      ? Math.max(...thresholdMultipliers.map(m => m.defaultMultiplier ?? 0))
      : 1;

  let highestOvertimeTierName: string | undefined;
  if (thresholdMultipliers.length > 0) {
    const match = thresholdMultipliers.find(m => m.defaultMultiplier === highestOvertimeRate);
    if (match) {
      highestOvertimeTierName = match.name;
    } else {
      const sorted = [...thresholdMultipliers].sort((a, b) => (b.defaultMultiplier ?? 0) - (a.defaultMultiplier ?? 0));
      highestOvertimeTierName = sorted[0]?.name;
    }
  }

  return {
    thresholdMultipliers,
    highestOvertimeRate,
    highestOvertimeTierName,
    maxExtendedTiers: thresholdMultipliers.length,
  };
}

/**
 * Derives overtime ladder metadata from the rate multipliers array.
 * Shared across all surcharge config forms that render ExtendedOvertimeSection.
 *
 * @param rateMultipliers — full multiplier array (including STRAIGHT_TIME)
 * @param highestOvertimeRateOverride — optional prop override (ShortTurn client context)
 */
export function useOvertimeLadderInfo(
  rateMultipliers: RateMultiplier[],
  highestOvertimeRateOverride?: number | null,
): OvertimeLadderInfo {
  return useMemo(
    () => getOvertimeLadderInfo(rateMultipliers, highestOvertimeRateOverride),
    [rateMultipliers, highestOvertimeRateOverride],
  );
}
